import React, { useState, useEffect } from "react";
import CertificationForm from "./CertificationForm";
import CertificationCard from "./CertificationCard";
import { sectionsAPI } from "../../../services/sectionsAPI";
import { formatAPIError } from "../../../utils/validation";
import "./styles/CertificationSection.css";

const CertificationSection = () => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCertification, setEditingCertification] = useState(null);

  // bulk selection state (kept, but UI is simplified to match Education)
  const [selectedIds, setSelectedIds] = useState([]);

  // custom delete confirmation (same pattern as EducationSection)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);

  useEffect(() => {
    loadCertifications();
  }, []);

  const loadCertifications = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await sectionsAPI.certification.getAll();
      setCertifications(response.data || []);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCertification = () => {
    setEditingCertification(null);
    setIsFormOpen(true);
  };

  const handleEditCertification = (certification) => {
    setEditingCertification(certification);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      if (editingCertification) {
        await sectionsAPI.certification.update(editingCertification.id, formData);
        setSuccess("✅ Certification updated successfully!");
      } else {
        await sectionsAPI.certification.add(formData);
        setSuccess("✅ Certification added successfully!");
      }

      await loadCertifications();
      setIsFormOpen(false);
      setEditingCertification(null);

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // selection helpers (for potential bulk delete in future)
  const handleSelectCertification = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // open custom confirm dialog (single or bulk)
  const askDeleteCertifications = (ids) => {
    const normalized = Array.isArray(ids) ? ids : [ids];
    if (!normalized.length) return;
    setDeleteIds(normalized);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeleteIds([]);
  };

  const confirmDelete = async () => {
    if (!deleteIds.length) return;
    try {
      setError("");
      setSuccess("");
      await sectionsAPI.certification.delete(deleteIds);
      setSuccess(
        `✅ ${
          deleteIds.length === 1 ? "Certification" : "Certifications"
        } deleted successfully!`
      );
      setSelectedIds([]);
      await loadCertifications();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      closeConfirm();
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCertification(null);
  };

  if (loading) {
    return (
      <div className="certification-section">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading certifications...</p>
          <p className="loading-subtext">
            Manage your professional certifications and licenses.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="certification-section">
      {/* Header + Add button (mirrors education header) */}
      <div className="certification-header">
        <h2 className="certification-heading">Certification</h2>
        <button
          type="button"
          className="certification-add-btn"
          onClick={handleAddCertification}
        >
          <span className="certification-add-plus">+</span>
          Add Certification
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span>✅</span>
          <span>{success}</span>
        </div>
      )}

      {/* Empty state */}
      
        <div className="certification-list">
          {certifications.map((cert) => (
            <CertificationCard
              key={cert.id}
              certification={cert}
              isSelected={selectedIds.includes(cert.id)}
              onSelect={() => handleSelectCertification(cert.id)}
              onEdit={() => handleEditCertification(cert)}
              onDelete={() => askDeleteCertifications(cert.id)}
            />
          ))}
        </div>
      

      {/* Form modal */}
      {isFormOpen && (
        <CertificationForm
          certification={editingCertification}
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          loading={formLoading}
        />
      )}

      {/* Custom confirmation dialog (same style as education) */}
      {confirmOpen && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3 className="confirm-title">Delete certification</h3>
            <p className="confirm-body">
              This action cannot be undone. This will permanently delete{" "}
              {deleteIds.length === 1
                ? "this certification entry."
                : "these certification entries."}
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn confirm-cancel"
                onClick={closeConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationSection;
